import { produce, enableMapSet } from 'immer';
import isArray from 'lodash/isArray';
import { SetState, create } from 'zustand';
import { VisemeNames } from '~/components/ChatAvatar/Canvas/Character/animationData/defaultOption';
import { lipsyncEn } from '~/components/ChatAvatar/Canvas/Character/animationData/lipsync-en';
import {
  ChatAvatarData,
  ChatAvatarResetActions,
  ChatAvatarSetActions,
  ChatAvatarStates,
  ChatAvatarStore,
  LipsyncViseme,
} from '~/types';

enableMapSet();

export const MAX_CHARACTER_LENGTH = 4;

export const initialChatAvatarData: ChatAvatarData = {
  expressionType: 'neutral',
  morphs: [],
  morphKeys: new Set(),
  isChangeExpression: false,
  tts: {
    // TODO: tts는 기본 on 상태인지 확인
    speakingOn: true,
    speakingText: '',
  },
  lipsyncViseme: undefined,
  avatarQueueArray: [],
  animations: [],
  currentAnimation: undefined,
  avatarQueue: undefined,
  queueMorphIndex: 0,
  animationState: {
    type: 'Idle',
  },
};

const initialChatAvatarState = (): ChatAvatarStates => ({
  character: {},
});

const chatAvatarSetActions = (
  set: SetState<ChatAvatarStore>,
  get: () => ChatAvatarStore,
): ChatAvatarSetActions => ({
  setInitialCharacterState: ({ id }) =>
    set(
      produce((state: ChatAvatarStates) => {
        state.character[id] = { ...initialChatAvatarData };
      }),
    ),

  setExpressionType: ({ id, type }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          state.character[id].expressionType = type;
          state.character[id].isChangeExpression = true;
        }
      }),
    ),
  setMorphs: ({ id, morphs }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          state.character[id].morphs.push(morphs);
        }
      }),
    ),
  setMorphKeys: ({ id, keys }) =>
    set(
      produce((state) => {
        if (state.character?.[id]) {
          const u = new Set(state.character[id].morphKeys);
          u.add(keys);
          state.character[id].morphKeys = new Set([...u]);
        }
      }),
    ),
  setNowSpeakingText: ({ id, text }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          state.character[id].tts.speakingText = text;
          const pre = lipsyncEn.preProcessText(text);
          const viseme = lipsyncEn.wordsToVisemes(pre);
          state.character[id].lipsyncViseme = viseme as LipsyncViseme;
        }
      }),
    ),
  getLipsyncViseme: ({ id }) =>
    produce((state: ChatAvatarStates) => {
      return state.character?.[id].lipsyncViseme;
    }),
  setAnimations: ({ id, animations }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          state.character[id].animations = animations;
        }
      }),
    ),
  setCurrentAnimation: ({ id, animation }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          state.character[id].currentAnimation = animation;
        }
      }),
    ),
  shiftAvatarQueue: ({ id }) => {
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          const q = state.character[id]?.avatarQueueArray.shift();
          state.character[id].avatarQueue = q;
        }
      }),
    );
    return get().character?.[id]?.avatarQueue;
  },
  setAvatarQueue: ({ id, queue }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          if (isArray(queue)) {
            state.character[id].avatarQueueArray.push(...queue);
          } else {
            state.character[id].avatarQueueArray.push(queue);
          }
        }
      }),
    ),
  deleteQueue: ({ id }) =>
    set(
      produce((state: ChatAvatarStates) => {
        if (state.character?.[id]) {
          state.character[id].avatarQueue = undefined;
          state.character[id].queueMorphIndex = 0;
        }
      }),
    ),
  setAnimationState: ({ id, state }) =>
    set(
      produce((s: ChatAvatarStates) => {
        if (s.character?.[id]) {
          s.character[id].animationState = state;
        }
      }),
    ),
});

const chatAvatarResetActions = (
  set: SetState<ChatAvatarStore>,
  get: () => ChatAvatarStore,
): ChatAvatarResetActions => ({
  resetMorph: ({ id }) =>
    set(
      produce((state: ChatAvatarStore) => {
        if (state.character?.[id]) {
          state.character[id].morphs = [];
          state.character[id].morphKeys = new Set();
        }
      }),
    ),
  resetExpression: ({ id }) =>
    set(
      produce((state: ChatAvatarStore) => {
        if (state.character?.[id]) {
          state.character[id].expressionType = 'neutral';
        }
      }),
    ),
  resetLips: ({ id }) =>
    set(
      produce((state: ChatAvatarStore) => {
        if (state.character?.[id]) {
          VisemeNames.forEach((v) => {
            state.character?.[id].morphs.forEach((morph) => {
              const mIndex = morph.morphTargetDictionary['viseme_' + v];
              if (mIndex) {
                morph.morphTargetInfluences[mIndex] = 0;
              }
            });
          });
        }
      }),
    ),
  resetAvatarState: () =>
    set(
      produce((state: ChatAvatarStore) => {
        state.character = {};
      }),
    ),
});

const useChatAvatarStore = create<ChatAvatarStore>()((set, get) => ({
  ...initialChatAvatarState(),
  ...chatAvatarSetActions(set, get),
  ...chatAvatarResetActions(set, get),
}));

export default useChatAvatarStore;
